import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './inlineChoiceInteraction.scss';
import constants from '../../../../qti/Constants';

const keys = constants.keyCodes;
const enterKeyCode = constants.allowedKeyCodesNumericInput.ENTER;
const offsets = {
    // padding for the wraper (for calculations on lines 103, 94)
    // 1. for re-calculation of the wraper minWidth if AlternativesMenu has multiple lines
    // 2. and to calculate width of the wrapper with paddings
    wrapperPadding: 30,
    // padding in the horizontal menu for calculations (lines 92, 101)
    // 1. Calculate width of the content in the menu
    // 2. Calculate left offset for wrapper if minContentWidth < contentWidth
    menuPadding: 15,
};

const AlternativesMenu = ({
    alternatives,
    callback,
    setIsChoiceInteractionMenuOpen,
    inlineChoiceInteractionRef,
    value,
    model,
}) => {
    const optionRefs = [];
    const isAttempted = value !== undefined && value !== '';

    // starting index for keyboard navigation
    let currentlySelectedIndex = 0;
    const wrapperRef = useRef(null);
    const firstOptionRef = useRef(null);

    const [resizeWidth, setResizeWidth] = useState(window.innerWidth);
    const [isMultipleLines, setIsMultipleLines] = useState(false);
    const [isElementSelected, setIsElementSelected] = useState(false);
    const [stylesState, setStylesState] = useState({
        minWidth: 0,
        left: 0,
        visibility: 'visible',
    });

    const onKeyDown = (keyEvent) => {
        // prevent default to do not pass such events to the DOM
        // prevent from going to the other elements of the page if menu is opened
        keyEvent.preventDefault();

        // find active element id in the options for opened menu
        const activeElementId = document.activeElement.id;
        const selectedIndex = optionRefs.findIndex(ref => ref.getAttribute('id') === activeElementId);
        currentlySelectedIndex = selectedIndex > -1 ? selectedIndex : 0;

        const direction = {
            right: {
                element: optionRefs[currentlySelectedIndex + 1],
                nextIndex: 1,
                startIndex: 0,
            },
            left: {
                element: optionRefs[currentlySelectedIndex - 1],
                nextIndex: -1,
                startIndex: optionRefs.length - 1,
            },
        };

        // navigation cases with overlapping to the end and start of the array of the options
        const keyHandlerObject = {
            [keys.RIGHT]: direction.right,
            [keys.DOWN]: direction.right,
            [keys.LEFT]: direction.left,
            [keys.UP]: direction.left,

            // check for the tab action and also check if it was a combined action with shift
            [constants.allowedKeyCodesNumericInput.TAB_KEY]: {
                element: optionRefs[keyEvent.shiftKey ? currentlySelectedIndex - 1 : currentlySelectedIndex + 1],
                nextIndex: keyEvent.shiftKey ? -1 : 1,
                startIndex: keyEvent.shiftKey ? optionRefs.length - 1 : 0,
            },
        };

        // if pressed keyCode exists in the object above we will check for the end of the direction
        // if we faced the end of the direction we will go to the starting point

        if (keyHandlerObject[keyEvent.keyCode]) {
            if (keyHandlerObject[keyEvent.keyCode].element) {
                keyHandlerObject[keyEvent.keyCode].element.focus();
                currentlySelectedIndex += keyHandlerObject[keyEvent.keyCode].nextIndex;
            } else {
                optionRefs[keyHandlerObject[keyEvent.keyCode].startIndex].focus();
                currentlySelectedIndex = keyHandlerObject[keyEvent.keyCode].startIndex;
                return;
            }
        }

        // handle escape press
        if (keyEvent.keyCode === keys.ESCAPE) {
            handleMenuClose();
        }

        // handle enter and space to submit choice
        if (keyEvent.keyCode === enterKeyCode || keyEvent.keyCode === keys.SPACE) {
            handleHorizontalMenuUserResponse(keyEvent);
        }
    };

    const handleMenuClose = () => {
        setIsChoiceInteractionMenuOpen(false);
        // set focus to the inlineChoiceInteraction button
        inlineChoiceInteractionRef.current.focus();
    };

    const handleHorizontalMenuUserResponse = (selectEvent) => {
        handleMenuClose();
        setIsElementSelected(selectEvent.target.value !== constants.noChoiceMadeOption);
        const optionValue = selectEvent.target.value === constants.noChoiceMadeOption ? [] : [selectEvent.target.value];

        callback(model.identifier, optionValue);
    };

    useEffect(() => {
        function handleClickOutside(clickEvent) {
            const toggleMenuCondition = wrapperRef.current &&
                !inlineChoiceInteractionRef.current.contains(clickEvent.target) &&
                !wrapperRef.current.contains(clickEvent.target);

            // execute focus on parent element after all microtasks on menu close
            // in our case we need to execute this after click outside of the menu
            if (toggleMenuCondition) setTimeout(handleMenuClose);
        }

        function handleResize() {
            setResizeWidth(window.innerWidth);
        }

        window.addEventListener('resize', handleResize);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const focusCorrectAlternative = useCallback(() => {
        const focusedElement = optionRefs.find(ref => ref.getAttribute('id') === value);
        if (focusedElement) {
            focusedElement.focus();
        } else if (!isElementSelected && (!value || value === constants.noChoiceMadeOption)) {
            firstOptionRef.current.focus();
        }
    }, [optionRefs]);

    useEffect(() => {
        const stylesStateCopy = { ...stylesState };

        if (!document.getElementById('sectionContent')) return;

        const minContentWidth = resizeWidth - inlineChoiceInteractionRef.current.getBoundingClientRect().left;
        const contentWidth = wrapperRef.current.offsetWidth + offsets.menuPadding;
        const wrapperWidth = document.getElementById('sectionContent').offsetWidth - offsets.wrapperPadding;

        if (wrapperWidth <= contentWidth) {
            setIsMultipleLines(true);
            stylesStateCopy.minWidth = `${Math.floor(minContentWidth - offsets.wrapperPadding)}px`;
        } else if (minContentWidth <= contentWidth) {
            stylesStateCopy.left = `${minContentWidth - contentWidth - offsets.menuPadding}px`;
        }

        setStylesState(stylesStateCopy);
        focusCorrectAlternative();
    }, [wrapperRef.current]);

    const getOptions = () => {
        const alternativeOptions = alternatives.map((alternative, index) => {
            const { content, identifier } = alternative;

            return (<option
                key={identifier}
                className={(isAttempted && identifier === value) ? styles.selected : ''}
                id={identifier}
                onClick={handleHorizontalMenuUserResponse}
                value={identifier}
                tabIndex={-1}
                ref={(element) => {
                    if (!index) {
                        firstOptionRef.current = element;
                    }

                    optionRefs[index + 1] = element;
                }}
            >
                {content}
            </option>);
        });

        const XMark = '\u2715';

        alternativeOptions.unshift(
            (<option
                key={constants.noChoiceMadeOption}
                value={constants.noChoiceMadeOption}
                onClick={handleHorizontalMenuUserResponse}
                label={XMark}
                tabIndex={-1}
                ref={(element) => {
                    optionRefs[0] = element;
                }}
            />)
        );

        return alternativeOptions;
    };

    return (<div
        tabIndex={-1}
        onKeyDown={onKeyDown}
        role="listbox"
        className={isMultipleLines ? styles.collapsedMenuMultiple : styles.collapsedMenu}
        ref={wrapperRef}
        style={stylesState}
    >
        {getOptions()}
    </div>);
};

AlternativesMenu.propTypes = {
    alternatives: PropTypes.array.isRequired,
    inlineChoiceInteractionRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    ]).isRequired,
    value: PropTypes.string.isRequired,
    model: PropTypes.object.isRequired,
    callback: PropTypes.func.isRequired,
    setIsChoiceInteractionMenuOpen: PropTypes.func.isRequired,
};

export default AlternativesMenu;
